/**
 * @fileoverview ComingSoon — generic placeholder panel for un-built subtabs.
 *
 * Rendered by the SuperAdmin → Operations subsystem tabs whose backend/UI
 * hasn't landed yet (Analytics rollup, PDF storage, Feedback→GitHub, Email).
 * Reuses the shared `.empty-state` styling so the placeholder shape matches
 * every other "not-yet-built" surface in the app — an operator opening one
 * of these tabs sees a consistent, calm panel rather than a blank div or a
 * bespoke one-off layout.
 *
 * Kept intentionally trivial: title + description only. No CTA, no icon,
 * no props for "expected ship date" — a subtab that grows enough context
 * to need those should be promoted to its own real component instead of
 * inflating this placeholder.
 */

import React from 'react';
import PropTypes from 'prop-types';

/**
 * Render a "Coming soon" placeholder panel.
 *
 * @param {Object} props
 * @param {string} props.title - Short heading (e.g. "Analytics rollup"). The
 *   " — coming soon" suffix is appended by this component; callers should
 *   pass just the subsystem name.
 * @param {string} props.description - One-sentence description of what this
 *   subsystem will eventually surface, so operators know what to expect.
 * @returns {JSX.Element}
 */
function ComingSoon({ title, description }) {
  return (
    <div
      className="empty-state"
      data-testid="coming-soon"
      role="status"
      aria-live="polite"
    >
      <h3 className="empty-state__title" data-testid="coming-soon-title">
        {title} — coming soon
      </h3>
      <p className="empty-state__description" data-testid="coming-soon-description">
        {description}
      </p>
    </div>
  );
}

ComingSoon.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

export default ComingSoon;
